import { isReportEnabled, REPORT_MIN_MATCHES } from "../../helpers/reportGate";
import { withBase } from "@/helpers/basePath";

/**
 * What's-new tour steps.
 *
 * The point of this tour is WHERE things moved, not that a release happened -
 * an agent who reads "we improved the feed" learns nothing, but one who is
 * shown the control learns where to click.
 *
 * Steps are surface-aware rather than one list with holes in it. On a phone the
 * selector and the quick-add live inside the drawer, so the mobile steps open
 * it and point at them there; on desktop they are in the rail. Same release,
 * same ideas, told where the agent will actually find them.
 *
 * Each step may set:
 *   target    CSS selector, a function returning an element, or null for a
 *             change with nothing to ring (the card centres instead). Use null
 *             sparingly - the spotlight is why this teaches better than a
 *             modal, and a run of centred steps is a modal with extra clicks.
 *   badge     optional short tag rendered as a highlighted pill after the title.
 *   available () => boolean deciding whether the step applies here. Defaults to
 *             "the target is visible", which is wrong for any step whose
 *             onEnter is the thing that makes the target exist.
 *   onEnter   runs before the step is measured, to put the UI into the state
 *             the step describes. Must be idempotent - going back re-enters it.
 *   visual    "match" renders the worked scoring example.
 *   gallery   [{src, caption}] renders a click-through of screenshots instead
 *             of a ring. For anything an agent cannot be shown in their own
 *             account - a match percentage needs matches, suggestions need a
 *             near miss - and every image is cropped to the cards alone, with
 *             no logo, rail or address bar, so a LAR or Momentum agent is not
 *             looking at another MLS's chrome.
 *
 * Bump RELEASE_KEY when a new release should re-trigger the tour. It is the
 * whole of the "has this agent seen it" identity - both stores key off it - so
 * an agent who saw the August tour still gets the next one.
 */
export const RELEASE_KEY = "2026-09-29";

export const SEEN_STORAGE_KEY = `whatsNewSeen:${RELEASE_KEY}`;

/**
 * Identity of this tour's row in the per-user `Tutorials` table
 * (UserId, PageNo, Stat) - that table has no column per feature, so a tutorial
 * simply IS its PageNo. It is what makes "seen" outlive a cleared cache:
 * clearing storage signs the agent out, and the next login reads the row back.
 *
 * Derived from RELEASE_KEY rather than hand-picked. Pages 1-10 belong to the
 * older tutorials and the next one added will take 11, so choosing "the next
 * free int" for something that recurs every release means re-checking that
 * registry each time - and two features picking the same number fail silently,
 * each suppressing the other. "2026-08" -> 202608 sits far outside the range
 * anyone allocates by hand, and bumping RELEASE_KEY carries the row with it.
 * A mid-month re-show appends the day instead ("2026-09-29" -> 20260929); it is
 * still one int. PageNo is `int`, so either form has room until the year 9999.
 */
export const RELEASE_TUTORIAL_PAGE = Number(RELEASE_KEY.replace(/-/g, ""));

// The array checkTutorialDisplayStatus/saveTutorialStatusToLocal keep, mirroring
// the per-user Tutorials rows.
export const TUTORIAL_PAGES_KEY = "tutorialDisplayedPages";

/**
 * Has this agent already been shown this release's tour?
 *
 * Reads the mirror array directly rather than calling checkTutorialDisplayStatus,
 * which looks like the obvious helper but is not: it short-circuits to false on
 * any MLS-restricted tenant, to keep the legacy Gitcha first-time overlays away
 * from BLS seats. Routed through it, this tour reads as "already seen" forever
 * on precisely the tenants it exists for.
 */
export const hasSeenRelease = () => {
  try {
    const pages = JSON.parse(localStorage.getItem(TUTORIAL_PAGES_KEY));
    if (Array.isArray(pages) && pages.includes(RELEASE_TUTORIAL_PAGE))
      return true;
    return localStorage.getItem(SEEN_STORAGE_KEY) === "1";
  } catch {
    // Unreadable storage - treat as seen rather than reopening on every load.
    return true;
  }
};

// Where the release article lives, for the "See everything that changed" link.
export const RELEASE_ARTICLE_URL =
  "https://thebuyerlistingservice.com/releases/september-2026";

const visible = (sel) => {
  const el = document.querySelector(sel);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return el.offsetParent !== null && r.width > 0 && r.height > 0;
};

// The mobile bar is rendered on desktop too and merely hidden, so this asks
// whether it is actually showing, not whether it exists.
const onMobile = () => visible(".feed-mobile-bar");

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const openMatchesDrawer = async () => {
  if (!document.querySelector(".offcanvas.feed-drawer")) {
    const segs = [...document.querySelectorAll(".feed-mobile-bar__seg")];
    const matches = segs.find((s) => /match/i.test(s.textContent || ""));
    (matches || segs[segs.length - 1])?.click();
    await wait(560);
  }
};

// Must be VISIBLE. Both the rail and the mobile drawer render an accordion, so
// an unscoped lookup happily returns a hidden one and the ring collapses to a
// 16px box in the corner.
const financingSection = () =>
  [...document.querySelectorAll(".accordion-item")].find((i) => {
    if (!/financing/i.test(i.textContent || "")) return false;
    const r = i.getBoundingClientRect();
    return i.offsetParent !== null && r.width > 0 && r.height > 0;
  }) || null;

const openFinancing = async () => {
  const item = financingSection();
  const btn = item?.querySelector(".accordion-button");
  if (btn && btn.classList.contains("collapsed")) {
    btn.click();
    await wait(340);
  }
};

// Where the report button is, or would be. Until a property is selected the
// demand panel shows a greyed "Download Report" placeholder in its place, so the
// ring lands on the spot the agent will use rather than on nothing. Only one of
// the two exists at a time, but the rail and the mobile drawer can both be in the
// DOM with one hidden, so take the first one that is actually showing.
const REPORT_TARGET =
  '[data-tour="download-report-match"], .demand-teaser__btn';

const isShowing = (el) => {
  const r = el.getBoundingClientRect();
  return el.offsetParent !== null && r.width > 0 && r.height > 0;
};

const reportTarget = () =>
  [...document.querySelectorAll(REPORT_TARGET)].find(isShowing) || null;

const REPORT_STEP_COPY = {
  title: "Download a Buyer Match Report",
  description: `Select a property you have added as a prospect. Once ${REPORT_MIN_MATCHES} buyers match it, this button downloads a PDF of who is looking, what they want and what they can spend - ready to hand a seller.`,
};

// Shared closer: matching quality is not a control on either surface, so the
// card carries a worked example instead of a ring.
const MATCHING_STEP = {
  id: "matching-upgrade",
  target: null,
  // No synthetic score card alongside these - the screenshots already show a
  // real percentage, and two different numbers in one card invites the reader
  // to work out which is the real one.
  gallery: [
    {
      src: withBase("/assets/whats-new/matches.jpg"),
      caption: "Every match carries a score, so you know which to call first.",
    },
    {
      src: withBase("/assets/whats-new/suggestions.jpg"),
      caption:
        "Nothing an exact fit? You get the near misses ranked, not an empty screen.",
    },
  ],
  title: "Our matching just got an upgrade",
  description:
    "Every Want-Listing now scores against your properties on location, budget, size and must-haves - and shows which of those it actually hit. Search a city and you only see buyers whose own search area covers it.",
};

const GENIE_STEPS = [
  {
    id: "genie-ai",
    target: '[data-tour="genie-ai"]',
    placement: "right",
    title: "Meet Genie",
    badge: "Beta",
    description:
      "Describe a buyer in your own words and it writes the Want-Listing: search area, budget, beds, size and timeline, saved as a draft you finish on the form. It also answers what your market is doing and who is looking in a ZIP.",
  },
  {
    id: "genie-ai-what-to-ask",
    target: '[data-tour="genie-ai"]',
    placement: "right",
    title: "Ask it the way you would ask a colleague",
    description:
      "Try: “Show me matches for my newest Want-Listing.” “Store 123 Main Street as a prospect's property and find buyer matches.” “How many cash buyers are looking in 79424 in the next 3 to 4 months?” Genie drafts first and asks after, so you never start from a blank form.",
  },
];

const DESKTOP_STEPS = [
  ...GENIE_STEPS,
  {
    id: "property-selector",
    target: ".property-selector-card",
    placement: "right",
    title: "All your properties in one list",
    description:
      "Your active MLS listings and your prospects inventory in one place, each showing how many buyers match it right now. Search it instead of scrolling.",
  },
  {
    id: "quick-add",
    target: ".quick-add-property__input",
    placement: "right",
    title: "Get demand insights for a prospect's property",
    description:
      "Type the address and the details fill in. The property stays private to you - add it to see how many buyers are already looking, and walk into the appointment knowing.",
  },
  {
    id: "buyer-match-report",
    available: () => isReportEnabled() && !!reportTarget(),
    target: reportTarget,
    placement: "right",
    ...REPORT_STEP_COPY,
  },
  {
    id: "sort-filter",
    available: () => visible(".sort-filter-btn"),
    onEnter: async () => {
      if (!document.querySelector(".sort-filter-panel")) {
        document.querySelector(".sort-filter-btn")?.click();
        await wait(420);
      }
      await openFinancing();
    },
    target: financingSection,
    placement: "left",
    title: "Filter by financing and timeline",
    description:
      "Every filter lives in this one panel and applies the moment you choose it - so you can narrow the feed to cash buyers ready in the next month.",
  },
  MATCHING_STEP,
];

const MOBILE_STEPS = [
  {
    id: "genie-ai-mobile",
    target: '[data-tour="genie-ai"]',
    placement: "top",
    title: "Meet Genie",
    badge: "Beta",
    description:
      "Describe a buyer in your own words and it writes the Want-Listing, saved as a draft you finish on the form. It also answers what your market is doing and who is looking in a ZIP.",
  },
  {
    id: "m-bar",
    target: ".feed-mobile-bar",
    placement: "below",
    title: "Your market and matches, up top",
    description:
      "Everything you need is in this bar - the market you are searching, your matches, and your filters. No more hunting through Sort and Filter.",
  },
  {
    id: "m-selector",
    available: onMobile,
    onEnter: openMatchesDrawer,
    target: ".property-selector-card",
    placement: "below",
    title: "Your properties live under My matches",
    description:
      "Active MLS listings and your prospects inventory in one searchable list, each showing how many buyers match it right now.",
  },
  {
    id: "m-quick-add",
    available: onMobile,
    onEnter: openMatchesDrawer,
    target: ".quick-add-property__input",
    placement: "below",
    title: "Add a prospect's property from here too",
    description:
      "Type an address and the details fill in. It stays private to you - add it to see how many buyers are already looking before the listing appointment.",
  },
  {
    id: "m-buyer-match-report",
    available: () => isReportEnabled() && onMobile(),
    onEnter: openMatchesDrawer,
    target: reportTarget,
    placement: "below",
    ...REPORT_STEP_COPY,
  },
  {
    id: "m-filters",
    available: onMobile,
    onEnter: async () => {
      document.querySelector(".offcanvas.feed-drawer .btn-close")?.click();
      await wait(380);
      document.querySelector(".feed-mobile-bar__filters")?.click();
      await wait(560);
      await openFinancing();
    },
    target: financingSection,
    placement: "below",
    title: "Filter by financing and timeline",
    description:
      "Filters open from the bottom, stay collapsed until you need them, and show a running result count - so you can narrow to cash buyers ready in the next month.",
  },
  MATCHING_STEP,
];

/**
 * Puts back anything the tour opened. Steps open panels to show what is inside
 * them; without this the agent finishes and is left sitting in a filter panel
 * or a drawer they never opened, which reads as the tour having broken
 * something.
 */
export const snapshotUi = () => ({
  // Which side of the mobile segmented control was active before the tour
  // started. Step two taps My matches to reach the selector, which switches
  // the feed into match mode - with no property chosen that leaves an empty
  // feed behind once the tour ends.
  activeSeg:
    document.querySelector(".feed-mobile-bar__seg.is-active")?.textContent?.trim() ||
    null,
  // Whether the agent already had the drawer open before the tour touched it.
  // Without this, an agent who opened it themselves finishes the tour with it
  // closed - restoreUi unconditionally closing it reads as a bug it just
  // caused, not a restoration.
  drawerOpen: !!document.querySelector(".offcanvas.feed-drawer.show"),
});

export const restoreUi = async (snapshot) => {
  const sheetClose = document.querySelector(".offcanvas.feed-drawer .btn-close");
  if (sheetClose && !snapshot?.drawerOpen) {
    sheetClose.click();
    await wait(320);
  }
  if (document.querySelector(".sort-filter-panel")) {
    const close = document.querySelector(".sort-filter-panel__close");
    if (close) close.click();
    else document.querySelector(".sort-filter-btn")?.click();
    await wait(240);
  }

  const wanted = snapshot?.activeSeg;
  if (wanted) {
    const segs = [...document.querySelectorAll(".feed-mobile-bar__seg")];
    const active = segs.find((x) => x.classList.contains("is-active"));
    if (active && active.textContent.trim() !== wanted) {
      segs.find((x) => x.textContent.trim() === wanted)?.click();
    }
  }
};

export const getSteps = () => (onMobile() ? MOBILE_STEPS : DESKTOP_STEPS);
